// Pure builders for a forecast-lock snapshot. Kept free of any DB/React so the
// snapshot logic is unit-testable and matches the live screens exactly: a lock
// is just the engine's own output for a week, frozen.

import type { Customer, Hub, HubEvent } from "../types";
import { forecastCustomerHub } from "./engine";

/** One frozen forecast atom: the committed value for a customer at a hub on a date. */
export interface LockCell {
  customerId: string;
  hubId: string;
  date: string; // YYYY-MM-DD
  value: number;
}

/** One frozen origin→branch flow volume (drives the locked branch plan). */
export interface LockFlow {
  originHubId: string;
  branchId: string;
  volume: number;
}

/** The 7 dates (Sat→Fri) of an operational week starting at `weekStart`. */
export function weekDates(weekStart: string): string[] {
  const d = new Date(weekStart + "T12:00:00");
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(d);
    x.setDate(x.getDate() + i);
    return x.toISOString().slice(0, 10);
  });
}

/**
 * Snapshot every (customer, hub, date) forecast for the given dates, with the
 * live overrides + hub events applied — i.e. exactly what the planning screens
 * show right now. Only non-zero cells are kept (most customer/hub pairs are 0).
 * All hubs are scanned (not just a customer's allocations) so rerouted-IN volume
 * at a hub the customer doesn't normally serve is captured too.
 */
export function buildLockCells(
  customers: Customer[],
  hubs: Hub[],
  dates: string[],
  overrides: Map<string, number>,
  events: HubEvent[]
): LockCell[] {
  const cells: LockCell[] = [];
  for (const c of customers) {
    for (const h of hubs) {
      for (const d of dates) {
        const value = forecastCustomerHub(c, h.id, d, overrides, events);
        if (value > 0) cells.push({ customerId: c.id, hubId: h.id, date: d, value });
      }
    }
  }
  return cells;
}

/** Sum locked cells into per-(hub, date) totals: key `${hubId}|${date}` → value. */
export function lockedHubDayTotals(cells: LockCell[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const c of cells) {
    const k = `${c.hubId}|${c.date}`;
    m.set(k, (m.get(k) ?? 0) + c.value);
  }
  return m;
}

/** Sum locked cells into per-hub weekly totals: hubId → value. */
export function lockedHubWeekTotals(cells: LockCell[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const c of cells) m.set(c.hubId, (m.get(c.hubId) ?? 0) + c.value);
  return m;
}
